/* Módulo 6 en el navegador. El mismo detector que el servidor, corriendo antes
   de enviar: la data sensible no llega a la API. Lo que se reporta es que hubo
   un intento, en qué módulo y campo, de quién. Nunca el dato. */
import { detectarEnObjeto, SENSIBLE, type Deteccion } from "@aimak/shared/sensible";
import { ApiError, type Excepcion } from "./api";
import { estado } from "./estado";
import { esc, modal, toast } from "./ui";

let excepciones: Excepcion[] | null = null;
async function excepcionesVigentes(): Promise<Excepcion[]> {
  if (excepciones) return excepciones;
  try { const r = await fetch("/api/compliance/excepciones", { credentials: "include" }); excepciones = r.ok ? await r.json() : []; } catch { excepciones = []; }
  setTimeout(() => { excepciones = null; }, 5 * 60 * 1000);
  return excepciones!;
}
export const olvidarExcepciones = () => { excepciones = null; };

const MODULO_DE_RUTA: [RegExp, string][] = [[/^\/makai/, "makai"], [/^\/conocimiento/, "knowledge"], [/^\/horas|^\/ausencias/, "timesheet"], [/^\/(estimados|partidas|campanas|finanzas|clientes\/[^/]+\/(medios|fee))/, "finanzas"], [/^\/(solicitudes|usuarios|catalogo|feriados)/, "settings"], [/^\/memoria/, "makai"]];
const moduloDe = (ruta: string) => MODULO_DE_RUTA.find(([re]) => re.test(ruta))?.[1] ?? "pm";

const cubre = (e: Excepcion, tipo: string, modulo: string, campo: string) => {
  const u = estado.usuario();
  return e.tipo === tipo && (!e.modulo || e.modulo === modulo) && (!e.campo || e.campo === campo) && (!e.rol || e.rol === u?.rol) && (!e.usuarioId || e.usuarioId === u?.id);
};

async function reportar(ruta: string, body: unknown) {
  try { await fetch("/api" + ruta, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(body) }); } catch { /* el servidor lo verá igual si algo se cuela */ }
}

/** Lanza `dato_sensible` (y muestra el aviso) si el cuerpo lleva algo que no debe salir del navegador. */
export async function vigilarCuerpo(ruta: string, body: unknown) {
  if (!estado.me) return;
  const hallazgos = detectarEnObjeto(body);
  if (!hallazgos.length) return;
  const modulo = moduloDe(ruta);
  const exc = await excepcionesVigentes();
  const bloqueados: { campo: string; d: Deteccion }[] = [];
  for (const h of hallazgos) for (const d of h.detecciones) if (!exc.some((e) => cubre(e, d.tipo, modulo, h.campo))) bloqueados.push({ campo: h.campo, d });
  if (!bloqueados.length) return;
  // El ticket se abre solo, sin depender de que el usuario reporte. Sin el contenido.
  for (const b of bloqueados) void reportar("/compliance/intentos", { tipo: b.d.tipo, modulo, campo: b.campo, veces: b.d.veces });
  avisoBloqueo(modulo, bloqueados);
  throw new ApiError(422, "dato_sensible", "No se envió: " + bloqueados.map((b) => b.d.etiqueta.toLowerCase()).join(", ") + " en " + bloqueados.map((b) => b.campo).join(", ") + ".");
}

/** El modal que explica, no que regaña. Con dos salidas: editar sin el dato, o pedir excepción. */
export function avisoBloqueo(modulo: string, bloqueados: { campo: string; d: Deteccion }[]) {
  const primero = bloqueados[0]!;
  modal({
    titulo: "Esto no puede guardarse así", sub: "Se detectó data sensible antes de enviar. No salió del navegador: lo único que queda registrado es que hubo un intento, en qué campo y de quién.",
    cuerpo: '<div style="display:flex;flex-direction:column;gap:10px">' + bloqueados.map((b) => '<div class="aviso ' + (b.d.severidad === "alta" ? "crit" : "warn") + '"><b>' + esc(b.d.etiqueta) + "</b> · campo <i>" + esc(b.campo) + "</i>" + (b.d.veces > 1 ? " · " + b.d.veces + " veces" : "") + '<div style="margin-top:4px">' + esc(b.d.explicacion) + "</div></div>").join("") +
      '<div style="font-size:12px;color:var(--text3);line-height:1.5">Si es un código de campaña o un número de orden que parece tarjeta, repórtalo como falso positivo y IT ajusta la regla. Si tu caso necesita el dato de verdad (una cuenta de salud, por ejemplo), pide una excepción para este campo: queda con vigencia y con nombre.</div></div>',
    botones: [
      { t: "Editar sin el dato", cls: "primario" },
      { t: "Es un falso positivo", fn: () => { pedir("falso_positivo", modulo, primero); } },
      { t: "Pedir excepción", fn: () => { pedir("excepcion", modulo, primero); } },
    ],
  });
}

function pedir(clase: "excepcion" | "falso_positivo", modulo: string, b: { campo: string; d: Deteccion }) {
  setTimeout(() => modal({
    titulo: clase === "excepcion" ? "Pedir excepción" : "Reportar falso positivo", sub: clase === "excepcion" ? "IT la revisa. Si la concede, queda por campo y por rol, con vigencia, y se registra." : "IT revisa la regla. Los falsos positivos repetidos aparecen en el panel de patrones.",
    cuerpo: '<div class="aviso info">' + esc(b.d.etiqueta) + " · " + esc(modulo) + " · " + esc(b.campo) + '</div><div class="lbl">POR QUÉ</div><textarea id="cp-motivo" rows="3" placeholder="' + (clase === "excepcion" ? "Qué necesitas guardar y para qué. Sin el dato." : "Qué es en realidad lo que se detectó. Sin pegarlo.") + '"></textarea>',
    botones: [{ t: "Cancelar" }, { t: "Enviar a IT", cls: "primario", fn: async (m) => {
      const motivo = m.querySelector<HTMLTextAreaElement>("#cp-motivo")!.value.trim(); if (motivo.length < 3) { toast("Cuenta el motivo."); return false; }
      const d = detectarEnObjeto({ motivo }); if (d.length) { toast("<b>El motivo también lleva el dato.</b> Descríbelo sin pegarlo."); return false; }
      await reportar("/compliance/solicitudes", { clase, tipo: b.d.tipo, modulo, campo: b.campo, motivo }); toast("<b>Ticket abierto para IT.</b> Lo verás resuelto en tu bandeja de incidentes.");
    } }],
  }), 50);
}

export const etiquetaTipo = (t: string) => (SENSIBLE as Record<string, { etiqueta: string }>)[t]?.etiqueta ?? (t === "solicitud_excepcion" ? "Solicitud de excepción" : t === "falso_positivo_reportado" ? "Falso positivo reportado" : t);
